import { useState, useEffect, useCallback } from "react";
import { generateUUID } from "~/utils/uuid";
import type { ItemOrAbility } from "~/types/character";

/**
 * Hook for managing items with local edit state overlay
 *
 * Adds edit state (isEditing flag) on top of the items from context.
 * Syncs with context items while preserving edit state for items that still exist.
 *
 * @param items Items from character data
 * @param updateItems Function to update items in character data
 * @param onRequestDelete Callback to request delete confirmation (opens dialog)
 * @returns Items with edit state and manipulation functions
 */
export function useItemsWithEditState(
  items: { id: string; content: string }[],
  updateItems: (items: { id: string; content: string }[]) => void,
  onRequestDelete: (itemId: string, itemName: string) => void
) {
  const [itemsWithEditState, setItemsWithEditState] = useState<ItemOrAbility[]>(
    items.map((i) => ({ ...i, isEditing: false }))
  );

  // Update local items with edit state when context items change
  // Preserve existing edit state for items that still exist
  useEffect(() => {
    setItemsWithEditState((prevItems) => {
      // Build a map of existing edit states
      const editingMap = new Map(prevItems.map((i) => [i.id, i.isEditing]));
      // Apply to new items, preserving edit state where possible
      return items.map((i) => ({
        ...i,
        isEditing: editingMap.get(i.id) ?? false,
      }));
    });
  }, [items]);

  const addItem = useCallback(
    (tag = "", title = ""): void => {
      const id = generateUUID();
      const content = tag ? `${tag} ${title}`.trim() : title;
      updateItems([...items, { id, content }]);
      setItemsWithEditState([...itemsWithEditState, { id, content, isEditing: true }]);
    },
    [items, itemsWithEditState, updateItems]
  );

  const toggleEditItem = useCallback((id: string): void => {
    setItemsWithEditState((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, isEditing: !item.isEditing } : item
      )
    );
  }, []);

  const updateItemContent = useCallback(
    (id: string, content: string): void => {
      setItemsWithEditState((prevItems) =>
        prevItems.map((item) => (item.id === id ? { ...item, content } : item))
      );
      const updatedItems = items.map((item) =>
        item.id === id ? { ...item, content } : item
      );
      updateItems(updatedItems);
    },
    [items, updateItems]
  );

  const moveItem = useCallback(
    (id: string, direction: "up" | "down"): void => {
      const index = items.findIndex((i) => i.id === id);
      if (index === -1) return;
      const newIndex = index + (direction === "up" ? -1 : 1);
      if (newIndex < 0 || newIndex >= items.length) return;

      const newItems = [...items];
      const [moved] = newItems.splice(index, 1);
      newItems.splice(newIndex, 0, moved);
      updateItems(newItems);
    },
    [items, updateItems]
  );

  const handleReorder = useCallback(
    (activeId: string, overId: string): void => {
      if (activeId !== overId) {
        const oldIndex = items.findIndex((i) => i.id === activeId);
        const newIndex = items.findIndex((i) => i.id === overId);
        if (oldIndex === -1 || newIndex === -1) return;

        const newItems = [...items];
        const [moved] = newItems.splice(oldIndex, 1);
        newItems.splice(newIndex, 0, moved);
        updateItems(newItems);
      }
    },
    [items, updateItems]
  );

  const deleteItem = useCallback(
    (id: string, skipConfirm = false): void => {
      if (skipConfirm) {
        updateItems(items.filter((item) => item.id !== id));
        return;
      }
      const item = itemsWithEditState.find((i) => i.id === id);
      const itemName = item?.content.split("\n")[0].replace(/^\[(Item|Ability)\]/i, "").trim() || "this item";
      onRequestDelete(id, itemName);
    },
    [items, itemsWithEditState, updateItems, onRequestDelete]
  );

  return {
    itemsWithEditState,
    addItem,
    toggleEditItem,
    updateItemContent,
    moveItem,
    handleReorder,
    deleteItem,
  };
}
